import { z } from 'zod';
import {
  earliestVisitDate,
  firstVisitDay,
  formatVisitDate,
  meetsMinimumNotice,
} from '../../lib/fieldVisitDates';
import type { FieldVisitRequestInput, PosStatus, SamplesStatus } from '../../types/fieldVisit';

export const SAMPLES_STATUS_OPTIONS: Array<{ value: SamplesStatus; label: string }> = [
  { value: 'on_hand', label: 'On hand' },
  { value: 'to_order', label: 'To order' },
  { value: 'partial', label: 'Some on hand, some to order' },
];

export const POS_STATUS_OPTIONS: Array<{ value: PosStatus; label: string }> = [
  { value: 'on_hand', label: 'On hand' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'need_to_order', label: 'Need to order' },
  { value: 'not_available', label: 'Not available' },
  { value: 'not_needed', label: 'Not needed' },
];

const isSamplesStatus = (v: string): boolean => SAMPLES_STATUS_OPTIONS.some((o) => o.value === v);
const isPosStatus = (v: string): boolean => POS_STATUS_OPTIONS.some((o) => o.value === v);

const optionalEmail = z.string().trim().email('Enter a valid email address').or(z.literal(''));

// True when the first visit day is on or after the submission date but inside
// four weeks: the dates that need the short-notice override. Past dates have
// their own error, and no dates means nothing to check yet.
export const needsNoticeOverride = (
  submittedOn: string,
  visitDates: Array<{ start_date: string }>,
): boolean => {
  const firstDay = firstVisitDay(visitDates);
  return !!firstDay && firstDay >= submittedOn && !meetsMinimumNotice(submittedOn, firstDay);
};

// The form keeps every field as the input's own type (strings, booleans) and
// converts to the API shape in toRequestInput. The rules mirror
// FieldVisitRequest in the API, plus one the API doesn't have: no past dates.
export const buildFieldVisitRequestSchema = (submittedOn: string) =>
  z
    .object({
      division_name: z.string().trim().min(1, 'Division is required'),
      territory: z.string().trim().min(1, 'Territory is required'),
      manufacturer_id: z.number().nullable().refine((v) => v !== null, 'Choose a client'),
      client_rep_name: z.string().trim().min(1, 'Client representative is required'),
      client_rep_email: optionalEmail,
      regional_sales_manager_name: z.string(),
      regional_sales_manager_email: optionalEmail,
      primary_objective: z.string().trim().min(1, 'Primary objective is required'),
      visit_dates: z
        .array(
          z
            .object({ start_date: z.string(), end_date: z.string() })
            .superRefine((row, ctx) => {
              if (!row.start_date) {
                ctx.addIssue({ code: 'custom', path: ['start_date'], message: 'Pick a date' });
              } else if (row.start_date < submittedOn) {
                ctx.addIssue({ code: 'custom', path: ['start_date'], message: "Visit dates can't be in the past" });
              }
              if (row.start_date && row.end_date && row.end_date < row.start_date) {
                ctx.addIssue({ code: 'custom', path: ['end_date'], message: 'End date must be on or after the start date' });
              }
            }),
        )
        .min(1, 'Add at least one visit date'),
      notice_override: z.boolean(),
      override_reason: z.string(),
      samples_status: z.string().refine(isSamplesStatus, 'Choose a samples status'),
      samples_notes: z.string(),
      samples_acknowledged: z
        .boolean()
        .refine((v) => v, 'Confirm the samples will be in-house a week before the visit'),
      pos_status: z.string().refine(isPosStatus, 'Choose a POS status'),
      pos_notes: z.string(),
    })
    .superRefine((values, ctx) => {
      if (values.notice_override || !needsNoticeOverride(submittedOn, values.visit_dates)) return;

      const firstDay = firstVisitDay(values.visit_dates);
      const index = values.visit_dates.findIndex((d) => d.start_date === firstDay);
      ctx.addIssue({
        code: 'custom',
        path: ['visit_dates', index, 'start_date'],
        message: `Visits need four weeks' notice. Pick ${formatVisitDate(earliestVisitDate(submittedOn))} or later, or request a short-notice exception.`,
      });
    });

export type FieldVisitRequestFormValues = z.infer<ReturnType<typeof buildFieldVisitRequestSchema>>;

export const emptyFieldVisitRequestForm = (divisionName = ''): FieldVisitRequestFormValues => ({
  division_name: divisionName,
  territory: '',
  manufacturer_id: null,
  client_rep_name: '',
  client_rep_email: '',
  regional_sales_manager_name: '',
  regional_sales_manager_email: '',
  primary_objective: '',
  visit_dates: [{ start_date: '', end_date: '' }],
  notice_override: false,
  override_reason: '',
  samples_status: '',
  samples_notes: '',
  samples_acknowledged: false,
  pos_status: '',
  pos_notes: '',
});

const blankToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

// Form values (already validated) -> the API's create payload. The override is
// sent only when the dates need it, so a rep who turned it on and then picked a
// date four or more weeks out isn't counted as a short-notice exception. The
// API applies the same rule.
export const toRequestInput = (
  values: FieldVisitRequestFormValues,
  submittedOn: string,
): FieldVisitRequestInput => {
  const override = values.notice_override && needsNoticeOverride(submittedOn, values.visit_dates);
  return {
    manufacturer_id: values.manufacturer_id as number,
    division_name: values.division_name.trim(),
    territory: values.territory.trim(),
    client_rep_name: values.client_rep_name.trim(),
    client_rep_email: blankToNull(values.client_rep_email),
    regional_sales_manager_name: blankToNull(values.regional_sales_manager_name),
    regional_sales_manager_email: blankToNull(values.regional_sales_manager_email),
    primary_objective: values.primary_objective.trim(),
    samples_status: values.samples_status as SamplesStatus,
    samples_notes: blankToNull(values.samples_notes),
    samples_acknowledged: values.samples_acknowledged,
    pos_status: values.pos_status as PosStatus,
    pos_notes: blankToNull(values.pos_notes),
    notice_override: override,
    override_reason: override ? blankToNull(values.override_reason) : null,
    // A same-day end date is the same as no end date.
    visit_dates_attributes: values.visit_dates.map((d) => ({
      start_date: d.start_date,
      end_date: d.end_date && d.end_date !== d.start_date ? d.end_date : null,
    })),
  };
};
